import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { posthog } from "@/config/posthog.config";
import { POSTHOG } from "@/constants";

// Every valid palette id. `iq` is fully RETIRED — classic supersedes it (classic
// aliases the old brand scale), so `iq` is no longer a `DashTheme`, has no CSS
// block, and is not keyed by `CHART_PALETTES`. The only remnants are string-level
// back-compat migrations: a stored `"iq"` palette maps to classic (see
// `readStoredPalette`) and a legacy `"iq"` flag-cache value reads as the kill
// switch (see `readFlagCache`). User-facing selection uses `SELECTABLE_THEMES`.
export const DASH_THEMES = ["classic", "grain", "teal", "slate"] as const;
export type DashTheme = (typeof DASH_THEMES)[number];

// The user-selectable palette lineup, in display order. `iq` is intentionally
// absent (retired — see above); the picker maps over THIS array.
export const SELECTABLE_THEMES = [
  "classic",
  "grain",
  "teal",
  "slate",
] as const satisfies readonly DashTheme[];

export const DASH_THEME_LABELS: Record<DashTheme, string> = {
  grain: "Grain",
  classic: "Classic",
  teal: "Midnight Teal",
  slate: "Slate",
};

// Legacy persisted-preset key from the retired preset switcher. We stopped
// reading/writing it long ago and clear it on mount; do NOT reuse it.
const LEGACY_STORAGE_KEY = "sarasiq-dash-theme";

// The user's explicit palette choice, written by `setDashTheme`. Read
// synchronously at mount so the app paints the chosen palette immediately with
// no flash of the default. This is the ONLY key the picker persists to.
const PALETTE_STORAGE_KEY = "sarasiq-theme-palette";

// Cache of the last resolved `GRAIN_THEME` kill-switch state, read synchronously
// at mount so we know before PostHog loads whether the picker is disabled and
// the palette forced to classic. Written on every flag resolution. Historically
// this key cached a DashTheme ("grain"/"iq"); we still read those legacy values
// (grain ⇒ enabled, iq ⇒ kill-switch active) so a warm cache keeps working.
const FLAG_CACHE_KEY = "sarasiq-theme-flag";

// PostHog flag, now a KILL SWITCH (was the driver). FALSE → force `classic` and
// hide the picker (ignore the stored palette); TRUE or unresolved → the user's
// stored palette (or the default) governs and the picker is available.
const FLAG_KEY = POSTHOG.FEATURE_FLAGS.GRAIN_THEME;

// The "no surprises" default: everyone lands on the familiar old-prod palette.
const DEFAULT_THEME: DashTheme = "classic";

/**
 * Widened validator for a user-selectable palette (excludes the retired `iq`).
 * Replaces the old guard that hardcoded `iq|grain`; a stored value is honored
 * only if it's one of the current selectable ids.
 */
const isSelectableTheme = (value: unknown): value is DashTheme =>
  typeof value === "string" &&
  (SELECTABLE_THEMES as readonly string[]).includes(value);

/**
 * The user's stored palette choice, migrated and validated:
 *  - the retired `"iq"` (or any non-selectable value) maps to `classic`;
 *  - an unknown/blocked read returns `null` so the caller falls to the default.
 */
function readStoredPalette(): DashTheme | null {
  try {
    const raw = localStorage.getItem(PALETTE_STORAGE_KEY);
    if (raw === null) return null;
    if (isSelectableTheme(raw)) return raw;
    // Migration: `iq` is retired → classic; anything else stored is treated as
    // the default (classic) too. `null` here means "no valid choice".
    return "classic";
  } catch {
    return null;
  }
}

function writeStoredPalette(theme: DashTheme): void {
  try {
    localStorage.setItem(PALETTE_STORAGE_KEY, theme);
  } catch {
    // localStorage may be unavailable (private mode / SSR-ish); persistence is
    // best-effort, so a failure is non-fatal.
  }
}

/**
 * Last-resolved kill-switch state from the cache:
 *  - `false` → kill switch was active (flag OFF): force classic, hide picker;
 *  - `true`  → flag was ON: picker available, user choice governs;
 *  - `null`  → unknown (no cache / blocked): proceed as if enabled.
 * Tolerates the legacy DashTheme cache values (`iq` ⇒ false, `grain` ⇒ true).
 */
function readFlagCache(): boolean | null {
  try {
    const cached = localStorage.getItem(FLAG_CACHE_KEY);
    if (cached === "off" || cached === "iq") return false;
    if (cached === "on" || cached === "grain") return true;
    return null;
  } catch {
    return null;
  }
}

function writeFlagCache(enabled: boolean): void {
  try {
    localStorage.setItem(FLAG_CACHE_KEY, enabled ? "on" : "off");
  } catch {
    // best-effort (see writeStoredPalette)
  }
}

type ResolvedState = { theme: DashTheme; pickerEnabled: boolean };

/**
 * Initial state before PostHog resolves the flag (synchronous, flash-free):
 *  - if the kill switch is cached as active → force `classic`, picker hidden;
 *  - otherwise → the user's stored palette (or the `classic` default), picker on.
 * localdev is NOT special-cased any more: with no PostHog connection the flag is
 * unresolved, so local runs follow the same path (default classic, picker usable).
 */
function resolveInitialState(): ResolvedState {
  if (readFlagCache() === false) {
    return { theme: DEFAULT_THEME, pickerEnabled: false };
  }
  return { theme: readStoredPalette() ?? DEFAULT_THEME, pickerEnabled: true };
}

type DashboardThemeState = {
  dashTheme: DashTheme;
  setDashTheme: (theme: DashTheme) => void;
  /** `false` when the `GRAIN_THEME` kill switch is off — the picker must hide. */
  themePickerEnabled: boolean;
  /** `true` only inside a real {@link DashboardThemeProvider}; `false` for the default context. */
  hasProvider: boolean;
};

const DashboardThemeContext = createContext<DashboardThemeState>({
  dashTheme: DEFAULT_THEME,
  setDashTheme: () => null,
  themePickerEnabled: false,
  hasProvider: false,
});

export function DashboardThemeProvider({ children }: { children: React.ReactNode }) {
  const initial = useMemo(resolveInitialState, []);
  const [dashTheme, setDashThemeState] = useState<DashTheme>(initial.theme);
  const [themePickerEnabled, setThemePickerEnabled] = useState<boolean>(
    initial.pickerEnabled,
  );

  // Once a user picks a palette (picker / tests), that choice wins for the rest
  // of the session — a later flag resolution (still ON) must not clobber it. The
  // kill switch going OFF is the one exception: it force-classics AND clears this
  // flag (see the flag effect), so if the flag later flips back ON the stored
  // palette is re-applied instead of the user being stranded on classic.
  const manualOverrideRef = useRef(false);

  const setDashTheme = useCallback((theme: DashTheme) => {
    manualOverrideRef.current = true;
    writeStoredPalette(theme);
    setDashThemeState(theme);
  }, []);

  // One-time cleanup of the legacy persisted-preset key (see LEGACY_STORAGE_KEY).
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Resolve the `GRAIN_THEME` kill switch and keep it reactive.
  useEffect(() => {
    const applyFlag = () => {
      const enabled = posthog.isFeatureEnabled(FLAG_KEY);
      // `undefined` = flags not loaded yet / PostHog unavailable: keep the
      // current (cached or default) state rather than snapping to a guess.
      if (enabled === undefined) return;
      writeFlagCache(enabled);

      if (!enabled) {
        // Kill switch active: force classic and hide the picker, ignoring any
        // stored choice — unconditionally, even over a prior manual set. Also
        // clear the manual-override flag so that if the kill switch later flips
        // back ON in the same session, the flag-ON path below re-applies the
        // user's stored palette instead of leaving them stuck on classic.
        manualOverrideRef.current = false;
        setThemePickerEnabled(false);
        setDashThemeState(DEFAULT_THEME);
        return;
      }

      // Flag on: the picker is available and the user's choice governs. Only
      // (re)apply the stored/default palette when the user hasn't picked this
      // session (covers the case where the initial paint was forced classic by
      // a now-stale off-cache).
      setThemePickerEnabled(true);
      if (!manualOverrideRef.current) {
        setDashThemeState(readStoredPalette() ?? DEFAULT_THEME);
      }
    };

    // Fires once flags load and again on any change; safe if PostHog never loads.
    const unsubscribe = posthog.onFeatureFlags(applyFlag);
    return () => unsubscribe?.();
  }, []);

  // Stamp the palette on the mount root (`#root`) and `<body>`, not just
  // `div.App`. Sonner toasts mount at `<Toaster/>` (a SIBLING of `div.App`) and
  // Radix menus/popovers/tooltips portal to `document.body` (a SIBLING of
  // `#root`) — both are outside `div.App`, so `--dash-*` stamped only there would
  // never reach them. `#root`/`<body>` are ancestors of those layers and stay
  // descendants of `.dark` on <html>, so both the tokens and the
  // `.dark [data-dash-theme=…]` blocks keep resolving for portalled content too.
  useEffect(() => {
    const root = document.getElementById("root");
    root?.setAttribute("data-dash-theme", dashTheme);
    document.body.setAttribute("data-dash-theme", dashTheme);
  }, [dashTheme]);

  const value = useMemo(
    () => ({ dashTheme, setDashTheme, themePickerEnabled, hasProvider: true }),
    [dashTheme, setDashTheme, themePickerEnabled],
  );

  return (
    <DashboardThemeContext.Provider value={value}>
      {children}
    </DashboardThemeContext.Provider>
  );
}

/**
 * Current app palette. Safe to call outside the provider (returns the `classic`
 * default with `hasProvider: false` and `themePickerEnabled: false`) so shared
 * chart components keep working in chat. Classic aliases the IQ chart palette,
 * so the out-of-provider default renders identical chart colors to before.
 */
export const useDashboardTheme = () => useContext(DashboardThemeContext);
